// Pathway taxonomy for the public patient-prospect signup flow.
//
// Pre-login we ask ONE question: "What kind of care are you looking for?"
// Each pathway maps to a backend `provisionalPathway` enum value (see
// docs/care_orchestration_system.md) plus a finer-grained `specialtyTag`
// used internally for routing and dashboards. None of these are exposed
// as a clinic-brand list — brand assignment happens server-side later.
//
// The blurb is the only thing the user reads when picking; keep it short
// and pitched as "the care this finds you", not as the medical category.

export const PATHWAYS = [
  {
    id: 'mental-health',
    pathway: null, // resolved by sub-pick
    label: 'Mental health',
    blurb: 'Anxiety, low mood, sleep, focus, relationships — talk to a specialist who fits.',
    iconName: 'Brain',
    accent: '#7B61FF',
    subOptions: [
      {
        id: 'mental-psychiatry',
        pathway: 'PSYCHIATRY',
        specialtyTag: 'psychiatry',
        label: 'Psychiatry',
        blurb: 'For medication evaluation and management with a psychiatrist (MD).',
      },
      {
        id: 'mental-psychology',
        pathway: 'THERAPY',
        specialtyTag: 'psychology',
        label: 'Therapy / Psychology',
        blurb: 'For talk-therapy with a clinical psychologist or therapist.',
      },
    ],
  },
  {
    id: 'hormonal-health',
    pathway: 'BIOLOGICAL',
    specialtyTag: 'endocrinology',
    label: 'Hormonal health',
    blurb: 'Thyroid, diabetes, PCOS, weight, menstrual concerns — endocrinology.',
    iconName: 'HeartPulse',
    accent: '#E27D60',
  },
  {
    id: 'immune-system',
    pathway: 'BIOLOGICAL',
    specialtyTag: 'rheumatology',
    label: 'Immune system',
    blurb: 'Autoimmune, joint or skin flares, unexplained inflammation — rheumatology.',
    iconName: 'ShieldCheck',
    accent: '#41B883',
  },
  {
    id: 'child-growth',
    pathway: 'CHILD',
    specialtyTag: 'pediatrics',
    label: 'Child growth & care',
    blurb: 'Growth, development, behavior, nutrition or sleep concerns for a child.',
    iconName: 'Baby',
    accent: '#3D9ED7',
  },
  {
    id: 'not-sure',
    pathway: 'ASSESSMENT',
    specialtyTag: 'triage',
    label: 'Not sure / something else',
    blurb: 'Tell us what is going on; our care team will help you figure out next steps.',
    iconName: 'Puzzle',
    accent: '#8492A6',
  },
]

// Resolve a pathway selection (top-level + optional sub-option id) to the
// canonical { pathway, specialtyTag, label } the backend expects.
export function resolvePathway(topLevelId, subId) {
  const top = PATHWAYS.find((p) => p.id === topLevelId)
  if (!top) return null
  if (top.subOptions) {
    const sub = top.subOptions.find((s) => s.id === subId)
    if (!sub) return null
    return {
      pathway: sub.pathway,
      specialtyTag: sub.specialtyTag,
      topLabel: top.label,
      subLabel: sub.label,
    }
  }
  return {
    pathway: top.pathway,
    specialtyTag: top.specialtyTag,
    topLabel: top.label,
    subLabel: null,
  }
}

export const PROSPECT_DRAFT_KEY = 'patientProspectSignupDraft'
export const PROSPECT_DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000
